import React from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_WINDOW_HEIGHT,
  DEFAULT_WINDOW_SILL_HEIGHT } from "../../../../features/floorplan3d/geometry";
import { floorplannerStore } from "../../../../store/floorplannerStore";
import { WindowType } from "../../../../types/floorplannerTypes";
import DimensionField from "../components/DimensionField";
import LinkedDimensionsGroup from "../components/LinkedDimensionsGroup";
import MetricsSection from "../components/MetricsSection";
import SubsectionTitle from "../components/SubsectionTitle";
import View3DSection from "../components/View3DSection";
import UnitHeader from "../components/UnitHeader";
import type { PropertyPanelController } from "../types";
import {
  areNumbersEqual,
  degreesToRadians,
  getCommonMillimeterValue,
  getCommonValue,
  panelFieldInputClassName,
  panelFieldLabelClassName,
  panelGridFieldClassName,
  panelObjectTitleRowClassName,
  radiansToRoundedDegrees,
  toDisplayLength,
  toWorldFromDisplay,
} from "../propertyPanelUtils";

type WindowSectionProps = Pick<PropertyPanelController, "updateGlobalState"> & {
  selectedWindows: WindowType[];
  displayedWindows: WindowType[];
  is3DView: boolean;
};

const DEFAULT_MUNTIN_COUNT = 0;
const MUNTIN_COUNT_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8] as const;

const getWindowHeight = (window: WindowType) =>
  window.threeD?.height ?? DEFAULT_WINDOW_HEIGHT;
const getWindowBaseElevation = (window: WindowType) =>
  window.threeD?.baseElevation ?? DEFAULT_WINDOW_SILL_HEIGHT;

/**
 * Resolves horizontal spröjs/muntin count with the same fallbacks as the 3D mesh.
 * Defaults to 0. Legacy single-axis props map onto the matching orientation.
 */
const getWindowHorizontalMuntinCount = (window: WindowType) => {
  const explicit =
    window.muntinHorizontalCount ??
    window.spröjsHorizontalCount ??
    window.threeD?.muntinHorizontalCount;
  if (explicit != null) return Math.max(0, explicit);

  const legacyCount =
    window.muntinCount ?? window.spröjsCount ?? window.threeD?.muntinCount;
  if (legacyCount == null) return DEFAULT_MUNTIN_COUNT;

  const orientation =
    window.muntinOrientation ??
    window.spröjsOrientation ??
    window.threeD?.muntinOrientation ??
    "horizontal";
  return orientation === "horizontal"
    ? Math.max(0, legacyCount)
    : DEFAULT_MUNTIN_COUNT;
};

/**
 * Resolves vertical spröjs/muntin count with the same fallbacks as the 3D mesh.
 * Defaults to 0. Legacy single-axis props map onto the matching orientation.
 */
const getWindowVerticalMuntinCount = (window: WindowType) => {
  const explicit =
    window.muntinVerticalCount ??
    window.spröjsVerticalCount ??
    window.threeD?.muntinVerticalCount;
  if (explicit != null) return Math.max(0, explicit);

  const legacyCount =
    window.muntinCount ?? window.spröjsCount ?? window.threeD?.muntinCount;
  if (legacyCount == null) return DEFAULT_MUNTIN_COUNT;

  const orientation =
    window.muntinOrientation ??
    window.spröjsOrientation ??
    window.threeD?.muntinOrientation ??
    "horizontal";
  return orientation === "vertical"
    ? Math.max(0, legacyCount)
    : DEFAULT_MUNTIN_COUNT;
};

type MuntinSelectProps = {
  /** Short visible label under the Muntins group (e.g. Horizontal). */
  label: string;
  /** Full accessible name used by tests and screen readers. */
  ariaLabel: string;
  value: number | "";
  showMixedOption: boolean;
  mixedLabel: string;
  onChange: (count: number) => void;
};

/**
 * Compact muntin count select used in the 3D window property grid.
 */
const MuntinSelect: React.FC<MuntinSelectProps> = ({
  label,
  ariaLabel,
  value,
  showMixedOption,
  mixedLabel,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <label className={panelGridFieldClassName}>
      <span className={panelFieldLabelClassName}>
        <span className="min-w-0 max-w-full">{label}</span>
      </span>
      <select
        aria-label={ariaLabel}
        className={`${panelFieldInputClassName} cursor-pointer`}
        value={value}
        onChange={(event) => {
          const nextCount = Number.parseInt(event.target.value, 10);
          if (
            !MUNTIN_COUNT_OPTIONS.includes(
              nextCount as (typeof MUNTIN_COUNT_OPTIONS)[number],
            )
          ) {
            return;
          }
          onChange(nextCount);
        }}
      >
        {showMixedOption && <option value="">{mixedLabel}</option>}
        {MUNTIN_COUNT_OPTIONS.map((count) => (
          <option key={count} value={count}>
            {t("propertyPanel.muntinCountOption", { count })}
          </option>
        ))}
      </select>
    </label>
  );
};

const WindowSection: React.FC<WindowSectionProps> = ({
  selectedWindows,
  displayedWindows,
  updateGlobalState,
  is3DView }) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");
  const isMulti = selectedWindows.length > 1;

  return (
    <>
      {displayedWindows.map((window) => {
        const commonHorizontalMuntins = getCommonValue(
          selectedWindows,
          getWindowHorizontalMuntinCount,
        );
        const commonVerticalMuntins = getCommonValue(
          selectedWindows,
          getWindowVerticalMuntinCount,
        );
        // Windows default unlocked; only lock when explicitly true.
        const aspectLocked = Boolean(window.lockAspectRatio);

        return (
          <div key={`window-${window?.id}`}>
            <div className="border-b border-gray-300" />
            <div className="text-sm font-normal">
              <div className={panelObjectTitleRowClassName}>
                <UnitHeader
                  title={
                    isMulti
                      ? t("propertyPanel.multipleWindows", {
                          count: selectedWindows.length })
                      : t("propertyPanel.window")
                  }
                />
              </div>

              {/*
                In 3D, keep width + height linked under Metrics. Other 3D-only
                controls (placement, muntins) sit in compact two-column grids.
              */}
              <MetricsSection>
                {is3DView ? (
                  <LinkedDimensionsGroup
                    locked={aspectLocked}
                    onToggleLock={() => {
                      const newLockState = !aspectLocked;
                      selectedWindows.forEach((selectedWindow) => {
                        floorplannerStore.updateSymbolProperty(
                          selectedWindow.id,
                          "lockAspectRatio",
                          newLockState,
                        );
                        if (newLockState) {
                          const ratio =
                            (selectedWindow.windowLength || 1) /
                            (getWindowHeight(selectedWindow) || 1);
                          floorplannerStore.updateSymbolProperty(
                            selectedWindow.id,
                            "ratio",
                            ratio,
                          );
                        }
                      });
                    }}
                    widthField={
                      <DimensionField
                        label={t("propertyPanel.width")}
                        fieldClassName={panelGridFieldClassName}
                        value={
                          isMulti
                            ? (getCommonMillimeterValue(
                                selectedWindows,
                                (symbol) => symbol.windowLength,
                              ) ?? "")
                            : toDisplayLength(window?.windowLength || 0)
                        }
                        placeholder={isMulti ? mixed : undefined}
                        onChange={(nextValue) => {
                          const value = parseFloat(nextValue);
                          if (!Number.isFinite(value)) return;
                          updateGlobalState(
                            selectedWindows,
                            "windowLength",
                            value,
                          );
                          if (aspectLocked) {
                            const currentWidth = window.windowLength || 0;
                            if (currentWidth > 0) {
                              const scale =
                                toWorldFromDisplay(value) / currentWidth;
                              updateGlobalState(
                                selectedWindows,
                                "symbol3DHeight",
                                toDisplayLength(
                                  getWindowHeight(window) * scale,
                                ),
                              );
                            }
                          }
                        }}
                      />
                    }
                    lengthField={
                      <DimensionField
                        label={t("propertyPanel.height")}
                        fieldClassName={panelGridFieldClassName}
                        value={
                          isMulti
                            ? (getCommonMillimeterValue(
                                selectedWindows,
                                getWindowHeight,
                              ) ?? "")
                            : toDisplayLength(getWindowHeight(window))
                        }
                        placeholder={isMulti ? mixed : undefined}
                        onChange={(nextValue) => {
                          const value = parseFloat(nextValue);
                          if (!Number.isFinite(value)) return;
                          updateGlobalState(
                            selectedWindows,
                            "symbol3DHeight",
                            value,
                          );
                          if (aspectLocked) {
                            const currentHeight = getWindowHeight(window);
                            if (currentHeight > 0) {
                              const scale =
                                toWorldFromDisplay(value) / currentHeight;
                              updateGlobalState(
                                selectedWindows,
                                "windowLength",
                                toDisplayLength(
                                  (window.windowLength || 0) * scale,
                                ),
                              );
                            }
                          }
                        }}
                      />
                    }
                  />
                ) : (
                  <div className="grid items-end gap-2 py-2 pb-2 grid-cols-1">
                    <DimensionField
                      label={t("propertyPanel.width")}
                      fieldClassName={panelGridFieldClassName}
                      value={
                        isMulti
                          ? (getCommonMillimeterValue(
                              selectedWindows,
                              (symbol) => symbol.windowLength,
                            ) ?? "")
                          : toDisplayLength(window?.windowLength || 0)
                      }
                      placeholder={isMulti ? mixed : undefined}
                      onChange={(nextValue) =>
                        updateGlobalState(
                          selectedWindows,
                          "windowLength",
                          parseFloat(nextValue),
                        )
                      }
                    />
                  </div>
                )}
              </MetricsSection>

              {is3DView && (
                <>
                  <View3DSection>
                    <div className="grid grid-cols-2 items-end gap-2 py-1">
                      <DimensionField
                        label={t("propertyPanel.distanceFromFloor")}
                        fieldClassName={panelGridFieldClassName}
                        min={0}
                        value={
                          isMulti
                            ? (getCommonMillimeterValue(
                                selectedWindows,
                                getWindowBaseElevation,
                              ) ?? "")
                            : toDisplayLength(getWindowBaseElevation(window))
                        }
                        placeholder={isMulti ? mixed : undefined}
                        onChange={(nextValue) =>
                          updateGlobalState(
                            selectedWindows,
                            "symbol3DBaseElevation",
                            parseFloat(nextValue),
                          )
                        }
                      />
                      <DimensionField
                        label={t("propertyPanel.openAngle")}
                        fieldClassName={panelGridFieldClassName}
                        min={0}
                        max={90}
                        value={
                          isMulti
                            ? (getCommonValue(
                                selectedWindows,
                                (symbol) =>
                                  radiansToRoundedDegrees(symbol.openAngle ?? 0),
                                areNumbersEqual,
                              ) ?? "")
                            : radiansToRoundedDegrees(window?.openAngle ?? 0)
                        }
                        placeholder={isMulti ? mixed : undefined}
                        onChange={(nextValue) =>
                          updateGlobalState(
                            selectedWindows,
                            "openAngle",
                            degreesToRadians(parseFloat(nextValue)),
                          )
                        }
                      />
                    </div>
                  </View3DSection>

                  <SubsectionTitle>{t("propertyPanel.muntins")}</SubsectionTitle>
                  <div className="grid grid-cols-2 items-end gap-2 px-3 py-2 pb-3">
                    <MuntinSelect
                      label={t("propertyPanel.muntinOrientationHorizontal")}
                      ariaLabel={t("propertyPanel.muntinsHorizontal")}
                      value={
                        isMulti
                          ? (commonHorizontalMuntins ?? "")
                          : getWindowHorizontalMuntinCount(window)
                      }
                      showMixedOption={
                        isMulti && commonHorizontalMuntins === undefined
                      }
                      mixedLabel={mixed}
                      onChange={(count) =>
                        updateGlobalState(
                          selectedWindows,
                          "muntinHorizontalCount",
                          count,
                        )
                      }
                    />
                    <MuntinSelect
                      label={t("propertyPanel.muntinOrientationVertical")}
                      ariaLabel={t("propertyPanel.muntinsVertical")}
                      value={
                        isMulti
                          ? (commonVerticalMuntins ?? "")
                          : getWindowVerticalMuntinCount(window)
                      }
                      showMixedOption={
                        isMulti && commonVerticalMuntins === undefined
                      }
                      mixedLabel={mixed}
                      onChange={(count) =>
                        updateGlobalState(
                          selectedWindows,
                          "muntinVerticalCount",
                          count,
                        )
                      }
                    />
                  </div>
                  <div className="border-b border-gray-300" />
                </>
              )}
            </div>
            {!is3DView && <div className="border-b border-gray-300" />}
          </div>
        );
      })}
    </>
  );
};

export default WindowSection;
